import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SchedulerDragDropApiAbstract } from '../api/drag-drop';
import { SchedulerBaseItem } from '../models/item';
import { AbstractDragDrop } from '../../drag-drop/abstract/drag-drop';
import { SchedulerAuthService } from './auth.service';
import { SchedulerContainerService } from './container.service';
import { DragAndDropEvent } from '../../drag-drop/types/event';

@Injectable()
export abstract class SchedulerDragDropAbstract<
  Type extends SchedulerBaseItem
> extends AbstractDragDrop<Type> {
  protected readonly apiRouter = inject(SchedulerDragDropApiAbstract);
  protected readonly authService = inject(SchedulerAuthService);
  protected readonly translateService = inject(TranslateService);
  protected readonly containerService = inject(SchedulerContainerService);

  allowedToBeOrdered = true;
  async onDrop(event: DragAndDropEvent<Type>): Promise<Type[]> {
    return await super.onDrop(event);
  }

  async beforeDrop(event: DragAndDropEvent<Type>): Promise<boolean> {
    if (!this.authService.allowMovement()) {
      const authErr = this.translateService.instant('ERROR.AUTH.MOVE_ITEM');
      throw new Error(authErr);
    }
    return true;
  }

  async beforeDropOnSameList(event: DragAndDropEvent<Type>): Promise<boolean> {
    if (!this.authService.orderingItems()) {
      const authErr = this.translateService.instant('ERROR.AUTH.SORTING');
      throw new Error(authErr);
    }
    return true;
  }

  protected newVersionsOfLists(
    event: DragAndDropEvent<Type>,
    ordering: (val: Type[]) => Type[]
  ): Type[] {
    const isSameList = event.container.id === event.previousContainer.id;
    const currentList = this.containerService.getList(event.container.id);
    let newCurrentList: Type[] = [];
    if (currentList) {
      newCurrentList = ordering(
        isSameList
          ? [...currentList.value]
          : [
              ...currentList.value.filter(
                (item) => item.id !== event.item.data.id
              ),
              event.item.data,
            ]
      );
      currentList.next(newCurrentList);
    } else {
      newCurrentList = ordering(
        isSameList
          ? [...event.container.data]
          : [
              ...event.container.data.filter(
                (item) => item.id !== event.item.data.id
              ),
              event.item.data,
            ]
      );
    }
    if (!isSameList) {
      const previousList = this.containerService.getList(
        event.previousContainer.id
      );
      if (previousList) {
        previousList.next(
          previousList.value.filter((item) => item.id !== event.item.data.id)
        );
      }
    }
    return newCurrentList;
  }
}
